import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Divider,
  Link,
  Button,
  Checkbox,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { supabasePublic } from "../components/supabaseClient";
import { getTermsContent } from "../utils/termsContent";

function TermsOfService() {
  const navigate = useNavigate();
  const toast = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [termsAlreadyAccepted, setTermsAlreadyAccepted] = useState(false);
  const [isLoadingTermsStatus, setIsLoadingTermsStatus] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [termsData, setTermsData] = useState(null);

  // Check if user has already accepted terms and fetch user role
  useEffect(() => {
    const checkTermsStatus = async () => {
      try {
        const { data: { user } } = await supabasePublic().auth.getUser();

        if (!user) {
          setIsLoadingTermsStatus(false);
          return;
        }

        // Check database for terms_accepted status and role
        const { data, error } = await supabasePublic()
          .from("users")
          .select("terms_accepted, role")
          .eq("id", user.id)
          .single();

        if (error) {
          // If column doesn't exist, check localStorage as fallback
          const localTermsAccepted = localStorage.getItem("termsAccepted") === "true";
          setTermsAlreadyAccepted(localTermsAccepted);
        } else {
          // Check both database and localStorage (database takes precedence)
          const dbAccepted = data?.terms_accepted === true;
          const localAccepted = localStorage.getItem("termsAccepted") === "true";
          setTermsAlreadyAccepted(dbAccepted || localAccepted);

          // Set user role and get terms content
          if (data?.role) {
            setUserRole(data.role);
            const content = getTermsContent(data.role);
            setTermsData(content);
          }
        }
      } catch (error) {
        console.error("Error checking terms status:", error);
        // Fallback to localStorage
        const localTermsAccepted = localStorage.getItem("termsAccepted") === "true";
        setTermsAlreadyAccepted(localTermsAccepted);
      } finally {
        setIsLoadingTermsStatus(false);
      }
    };

    checkTermsStatus();
  }, []);

  const handleAcceptTerms = async () => {
    if (!accepted) {
      toast({
        title: "Acceptance Required",
        description: "Please check that you have read and agreed to the Terms of Service",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    try {
      const { data: { user } } = await supabasePublic().auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      // Update the user's terms_accepted status in the database (public schema)
      const { error } = await supabasePublic()
        .from("users")
        .update({ terms_accepted: true })
        .eq("id", user.id);

      if (error) {
        // If column doesn't exist, still accept terms but store in localStorage only
        if (error.code === '42703' || error.message?.includes('does not exist')) {
          console.warn("terms_accepted column doesn't exist - storing acceptance in localStorage only");
          localStorage.setItem("termsAccepted", "true");
          localStorage.setItem("termsAcceptedDate", new Date().toISOString());
        } else {
          throw error;
        }
      } else {
        // Also store in localStorage as a backup/cache
        localStorage.setItem("termsAccepted", "true");
        localStorage.setItem("termsAcceptedDate", new Date().toISOString());
      }
      
      toast({
        title: "Terms Accepted",
        description: "Thank you for accepting our Terms of Service.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Navigate to schedules page after a short delay to show the toast
      setTimeout(() => {
        navigate("/schedules");
      }, 500);
    } catch (error) {
      console.error("Error saving terms acceptance:", error);
      toast({
        title: "Error",
        description: "Failed to save your acceptance. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Show loading state if terms data hasn't loaded yet
  if (isLoadingTermsStatus || !termsData) {
    return (
      <Container maxW="4xl" py={10}>
        <VStack spacing={6} align="stretch">
          <Text textAlign="center" color="gray.600">Loading Terms of Service...</Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="4xl" py={10}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading as="h1" size="2xl" mb={4}>
            Terms of Service and Privacy Policy
          </Heading>
          <Text color="gray.600" fontSize="sm">
            Last Updated: {new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </Text>
        </Box>

        <Divider />

        <Box>
          <Text fontSize="md" color="gray.700" mb={6}>
            {termsData.intro.welcome}
          </Text>
        </Box>

        {/* Render all sections dynamically from termsData */}
        {termsData.sections.map((section, index) => {
          // Helper function to render section content based on structure
          const renderSectionContent = (section) => {
            // Type 1: Simple section with just content string
            if (section.content && typeof section.content === 'string') {
              return <Text mb={4}>{section.content}</Text>;
            }

            // Type 2: Section with content array (multiple paragraphs)
            if (section.content && Array.isArray(section.content)) {
              return section.content.map((paragraph, idx) => (
                <Text key={idx} mb={4}>{paragraph}</Text>
              ));
            }

            // Type 3: Section with intro, features, and disclaimer (Service Description)
            if (section.intro && section.features) {
              return (
                <>
                  <Text mb={4}>{section.intro}</Text>
                  <VStack align="stretch" spacing={2} ml={4} mb={4}>
                    {section.features.map((feature, idx) => (
                      <Text key={idx}>• {feature}</Text>
                    ))}
                  </VStack>
                  {section.disclaimer && <Text>{section.disclaimer}</Text>}
                </>
              );
            }

            // Type 4: Section with subsections (Data Privacy, Account Responsibilities, etc.)
            if (section.sections && Array.isArray(section.sections)) {
              return section.sections.map((subsection, idx) => (
                <Text key={idx} mb={4}>
                  <strong>{subsection.subtitle}</strong> {subsection.content}
                </Text>
              ));
            }

            // Type 5: Account Responsibilities (special structure)
            if (section.accountCreation || section.accountSecurity || section.userConduct) {
              return (
                <>
                  {section.accountCreation && (
                    <Text mb={4}>
                      <strong>3.1 Account Creation:</strong> {section.accountCreation}
                    </Text>
                  )}
                  {section.accountSecurity && (
                    <Text mb={4}>
                      <strong>3.2 Account Security:</strong> {section.accountSecurity}
                    </Text>
                  )}
                  {section.userConduct && (
                    <>
                      <Text mb={4}>
                        <strong>3.3 User Conduct:</strong> {section.userConduct}
                      </Text>
                      {section.conductItems && (
                        <VStack align="stretch" spacing={2} ml={4} mb={4}>
                          {section.conductItems.map((item, idx) => (
                            <Text key={idx}>• {item}</Text>
                          ))}
                        </VStack>
                      )}
                    </>
                  )}
                </>
              );
            }

            // Type 6: Intellectual Property (special structure)
            if (section.serviceContent || section.userContent || section.feedback) {
              return (
                <>
                  {section.serviceContent && (
                    <Text mb={4}>
                      <strong>5.1 Service Content:</strong> {section.serviceContent}
                    </Text>
                  )}
                  {section.userContent && (
                    <Text mb={4}>
                      <strong>5.2 User Content:</strong> {section.userContent}
                    </Text>
                  )}
                  {section.feedback && (
                    <Text mb={4}>
                      <strong>5.3 Feedback:</strong> {section.feedback}
                    </Text>
                  )}
                </>
              );
            }

            // Type 7: Service Availability (special structure)
            if (section.availability || section.updates) {
              return (
                <>
                  {section.availability && (
                    <Text mb={4}>
                      <strong>8.1 Availability:</strong> {section.availability}
                    </Text>
                  )}
                  {section.updates && (
                    <Text mb={4}>
                      <strong>8.2 Updates:</strong> {section.updates}
                    </Text>
                  )}
                </>
              );
            }

            // Type 8: Contact Info (special structure)
            if (section.details) {
              return (
                <>
                  <Text mb={4}>{section.content}</Text>
                  <VStack align="stretch" spacing={2} ml={4} mb={4}>
                    {section.details.email && (
                      <Text>
                        <strong>Email:</strong>{" "}
                        <Link href={`mailto:${section.details.email}`} color="blue.500">
                          {section.details.email}
                        </Link>
                      </Text>
                    )}
                    {section.details.company && (
                      <Text>
                        <strong>Company:</strong> {section.details.company}
                      </Text>
                    )}
                  </VStack>
                </>
              );
            }

            return null;
          };

          return (
            <Box key={index}>
              <Heading as="h2" size="lg" mb={3}>
                {section.title}
              </Heading>
              {renderSectionContent(section)}
            </Box>
          );
        })}

        <Divider />

        {/* Privacy Policy Section */}
        <Box>
          <Heading as="h1" size="2xl" mb={4}>
            Privacy Policy
          </Heading>
          <Text color="gray.600" fontSize="sm" mb={6}>
            Last Updated: {new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </Text>

          <Text fontSize="md" color="gray.700" mb={6}>
            VitaMed AI ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use Magic Notes.
          </Text>

          <Box mb={6}>
            <Heading as="h2" size="lg" mb={3}>1. Information We Collect</Heading>
            <Text mb={4}><strong>1.1 Account Information:</strong> When you register, we collect your name, email address, and professional role to create and manage your account.</Text>
            <Text mb={4}><strong>1.2 Usage Data:</strong> We collect information about how you interact with the Service, including features used, session duration, and device/browser information, to improve performance and user experience.</Text>
            <Text mb={4}><strong>1.3 Audio and Clinical Data:</strong> We process audio recordings and the clinical notes generated from them solely to provide the transcription and documentation features you request. This data may constitute Protected Health Information (PHI) under HIPAA.</Text>
          </Box>

          <Box mb={6}>
            <Heading as="h2" size="lg" mb={3}>2. How We Use Your Information</Heading>
            <Text mb={4}><strong>2.1 Service Delivery:</strong> To provide, operate, and maintain the Service, including processing audio and generating clinical notes.</Text>
            <Text mb={4}><strong>2.2 Improvement:</strong> To analyze usage patterns and improve the accuracy, reliability, and features of the Service.</Text>
            <Text mb={4}><strong>2.3 Communications:</strong> To send you service-related notices, updates, and support responses. We do not send marketing emails without your explicit consent.</Text>
            <Text mb={4}><strong>2.4 Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes, including HIPAA obligations.</Text>
          </Box>

          <Box mb={6}>
            <Heading as="h2" size="lg" mb={3}>3. How We Share Your Information</Heading>
            <Text mb={4}><strong>3.1 No Sale of Data:</strong> We do not sell, rent, or share your personal information or patient data with third parties for advertising or marketing purposes.</Text>
            <Text mb={4}><strong>3.2 Service Providers:</strong> We may share data with trusted third-party vendors (e.g., cloud infrastructure, AI processing providers) who assist in delivering the Service, under strict confidentiality agreements and, where required, a signed Business Associate Agreement (BAA).</Text>
            <Text mb={4}><strong>3.3 Legal Requirements:</strong> We may disclose your information if required by law, court order, or governmental authority.</Text>
          </Box>

          <Box mb={6}>
            <Heading as="h2" size="lg" mb={3}>4. Data Security</Heading>
            <Text mb={4}>We implement industry-standard administrative, physical, and technical safeguards to protect your data, including encryption at rest and in transit. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.</Text>
          </Box>

          <Box mb={6}>
            <Heading as="h2" size="lg" mb={3}>5. Data Retention</Heading>
            <Text mb={4}>We retain your data only as long as necessary to provide the Service or as required by applicable law. Upon account deletion, we will delete or anonymize your data within a reasonable timeframe, subject to legal retention obligations.</Text>
          </Box>

          <Box mb={6}>
            <Heading as="h2" size="lg" mb={3}>6. Your Rights</Heading>
            <Text mb={4}><strong>6.1 Access and Correction:</strong> You may request access to or correction of your personal information by contacting us at support@magicnotes.ai.</Text>
            <Text mb={4}><strong>6.2 Account Deletion:</strong> You may request deletion of your account and associated data at any time by contacting support@magicnotes.ai. We will process deletion requests in accordance with applicable law.</Text>
            <Text mb={4}><strong>6.3 HIPAA Rights:</strong> If you are a patient whose PHI is processed through the Service, please contact your healthcare provider to exercise your rights under HIPAA.</Text>
          </Box>

          <Box mb={6}>
            <Heading as="h2" size="lg" mb={3}>7. Cookies and Tracking</Heading>
            <Text mb={4}>We use session cookies and similar technologies solely to maintain your login session and Service functionality. We do not use tracking cookies for advertising purposes.</Text>
          </Box>

          <Box mb={6}>
            <Heading as="h2" size="lg" mb={3}>8. Changes to This Privacy Policy</Heading>
            <Text mb={4}>We may update this Privacy Policy from time to time. We will notify you of material changes via email or an in-app notice at least 30 days before the changes take effect. Continued use of the Service after the effective date constitutes acceptance of the updated policy.</Text>
          </Box>

          <Box mb={6}>
            <Heading as="h2" size="lg" mb={3}>9. Contact Us</Heading>
            <Text mb={2}>If you have any questions about this Privacy Policy, please contact us at:</Text>
            <Text><strong>Email:</strong> support@magicnotes.ai</Text>
            <Text><strong>Company:</strong> VitaMed AI</Text>
          </Box>
        </Box>

        <Divider />

        <Box textAlign="center" py={4}>
          {isLoadingTermsStatus ? (
            <Text color="gray.600">Loading...</Text>
          ) : termsAlreadyAccepted ? (
            <VStack spacing={4}>
              <Box
                p={4}
                borderRadius="md"
                bg="green.50"
                borderWidth="1px"
                borderColor="green.200"
              >
                <Text fontSize="lg" fontWeight="semibold" color="green.700">
                  ✓ Terms Accepted
                </Text>
                <Text fontSize="sm" color="green.600" mt={2}>
                  You have already accepted the Terms of Service and Privacy Policy.
                </Text>
              </Box>
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                size="md"
              >
                Go Back
              </Button>
            </VStack>
          ) : (
            <VStack spacing={4}>
              <Checkbox
                isChecked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                colorScheme="blue"
                size="lg"
              >
                <Text fontSize="md">
                  I have read and agree to the Terms of Service and Privacy Policy
                </Text>
              </Checkbox>
              <VStack spacing={3}>
                <Button
                  colorScheme="blue"
                  onClick={handleAcceptTerms}
                  size="lg"
                  isLoading={isSaving}
                  loadingText="Saving..."
                  isDisabled={!accepted || isSaving}
                >
                  Accept & Continue
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate(-1)}
                  size="md"
                  isDisabled={isSaving}
                >
                  Go Back
                </Button>
              </VStack>
            </VStack>
          )}
        </Box>
      </VStack>
    </Container>
  );
}

export default TermsOfService;